import { NurseLayout } from "@/components/layout/nurse-layout";
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Award, ChevronRight, CheckCircle, Clock, Circle } from "lucide-react";

type PracticeLevel = "specialist" | "advanced" | "consultant";

const LEVEL_KEY = "rheumhub_practice_level";

interface DomainSummary {
  id: number;
  code: string;
  title: string;
  description: string;
  sortOrder: number;
  statementCount: number;
}

interface PortfolioSummary {
  domain: DomainSummary;
  total: number;
  achieved: number;
  inProgress: number;
  notStarted: number;
}

const LEVEL_LABELS: Record<PracticeLevel, string> = {
  specialist: "Specialist Practitioner",
  advanced: "Advanced Practitioner",
  consultant: "Consultant Practitioner",
};

const LEVEL_COLORS: Record<PracticeLevel, string> = {
  specialist: "bg-blue-50 border-blue-200 text-blue-800",
  advanced: "bg-purple-50 border-purple-200 text-purple-800",
  consultant: "bg-amber-50 border-amber-200 text-amber-800",
};

export default function CompetencyOverview() {
  const [level, setLevel] = useState<PracticeLevel>(() => {
    return (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist";
  });
  const [domains, setDomains] = useState<DomainSummary[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const changeLevel = (l: PracticeLevel) => {
    setLevel(l);
    localStorage.setItem(LEVEL_KEY, l);
    window.dispatchEvent(new CustomEvent("rheumhub-level-change", { detail: l }));
  };

  const loadPortfolio = async (l: PracticeLevel) => {
    const portfolioRes = await fetch(`/api/competency/portfolio?practiceLevel=${l}`);
    if (portfolioRes.ok) {
      const data = await portfolioRes.json();
      setPortfolio(Array.isArray(data) ? data : (data?.domains ?? []));
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const domainsRes = await fetch("/api/competency/domains");
        if (domainsRes.ok) setDomains(await domainsRes.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    loadPortfolio(level);
  }, [level]);

  const totalAchieved = portfolio.reduce((s, d) => s + d.achieved, 0);
  const totalStatements = portfolio.reduce((s, d) => s + d.total, 0);

  const getPortfolioForDomain = (domainId: number) =>
    portfolio.find((p) => p.domain.id === domainId);

  return (
    <NurseLayout title="Competency Portfolio">
      <div className="space-y-8">

        {/* Header */}
        <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Award className="h-8 w-8 text-teal-300" />
            <div>
              <h2 className="text-xl font-bold">RCN Rheumatology Competency Framework</h2>
              <p className="text-teal-200 text-sm">Second Edition, December 2024 · BSR Endorsed</p>
            </div>
          </div>
          <p className="text-teal-100 text-sm leading-relaxed mb-4">
            Track your professional development against the Royal College of Nursing competency framework for rheumatology nurses. Document evidence, record reflections, and generate a printable portfolio for NMC revalidation and appraisal.
          </p>
          {totalStatements > 0 && (
            <div className="bg-white/10 rounded-xl px-4 py-3 flex items-center gap-4 text-sm">
              <CheckCircle className="h-5 w-5 text-teal-300" />
              <span>{totalAchieved} of {totalStatements} competencies achieved at {LEVEL_LABELS[level]} level</span>
              <div className="flex-1 bg-white/20 rounded-full h-2 ml-2">
                <div
                  className="bg-teal-300 h-2 rounded-full transition-all"
                  style={{ width: `${totalStatements > 0 ? (totalAchieved / totalStatements) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Practice Level Selector */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <p className="text-sm text-slate-500 mb-3 font-medium">Select your practice level:</p>
          <div className="flex flex-wrap gap-2">
            {(["specialist", "advanced", "consultant"] as PracticeLevel[]).map((l) => (
              <button
                key={l}
                onClick={() => changeLevel(l)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  level === l
                    ? LEVEL_COLORS[l] + " ring-2 ring-offset-1 ring-current"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {LEVEL_LABELS[l]}
              </button>
            ))}
          </div>
        </div>

        {/* Domain Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {domains.map((d) => {
              const p = getPortfolioForDomain(d.id);
              const achieved = p?.achieved ?? 0;
              const inProgress = p?.inProgress ?? 0;
              const total = p?.total ?? d.statementCount;
              const pct = total > 0 ? Math.round((achieved / total) * 100) : 0;

              return (
                <Link key={d.id} href={`/nurse/competency/${d.id}`}>
                  <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <span className="text-xs font-mono text-slate-400">{d.code}</span>
                        <h3 className="font-bold text-slate-900 text-sm leading-snug mt-0.5 group-hover:text-teal-700 transition-colors">{d.title}</h3>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-teal-500 shrink-0 mt-1 transition-colors" />
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-2">{d.description}</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>{achieved}/{total} achieved</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div
                          className="bg-teal-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex gap-3 text-xs">
                        <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3 w-3" />{achieved} achieved</span>
                        <span className="flex items-center gap-1 text-amber-600"><Clock className="h-3 w-3" />{inProgress} in progress</span>
                        <span className="flex items-center gap-1 text-slate-400"><Circle className="h-3 w-3" />{total - achieved - inProgress} not started</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="flex gap-3">
          <Link href="/nurse/competency/plan">
            <button className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
              View Development Plan
            </button>
          </Link>
          <Link href="/nurse/competency/export">
            <button className="px-5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-sm font-medium transition-colors">
              Download / Print Portfolio
            </button>
          </Link>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Attribution:</strong> Competency framework content reproduced from "A Competency Framework for Rheumatology Nurses, Second Edition" © Royal College of Nursing 2024 (Publication code 011 572). BSR endorsed. For personal professional development use only — achievement of competencies should be validated by your line manager or senior peer assessor in accordance with your trust's appraisal process.
        </div>

      </div>
    </NurseLayout>
  );
}
